// Pure helpers for the Lead Agent usage/limit banner (C8 / RET-1344). Surface +
// size resolution is REUSED from the sibling pre-acceptance banner so all the
// Lead Agent banners stay on the same routes/sizing rules — we only add the
// capacity fill helper and the thin render gate over the provider's role flags.

import moment from 'moment';
import 'moment/dist/locale/es-mx';
import {
  resolveBannerSize,
  resolveSurface,
} from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';
import type { tLeadAgentPaywallSurface } from 'src/hooks/analytics/leadAgentPaywallAnalytics.constants';

export { resolveBannerSize, resolveSurface };
export type { tLeadAgentBannerSize } from 'src/components/LeadAgentAcceptanceBanner/LeadAgentAcceptanceBanner.functions';

/**
 * Usage reset dates represent a billing CALENDAR DAY, encoded as the period's
 * UTC end-of-day boundary. Preserve that encoded date instead of converting
 * the instant to the viewer's timezone, which can roll east-of-UTC viewers
 * into the following day.
 */
const formatUsageResetDateWith = (
  resetDate: string | null,
  format: string,
): string | null => {
  if (!resetDate) return null;
  const date = moment.parseZone(resetDate);
  if (!date.isValid()) return null;
  return date.locale('es-mx').format(format);
};

export const formatUsageResetDate = (
  resetDate: string | null,
): string | null => {
  return formatUsageResetDateWith(resetDate, 'D [de] MMM YYYY');
};

export const formatUsageResetDateLong = (
  resetDate: string | null,
): string | null => {
  return formatUsageResetDateWith(resetDate, 'D [de] MMMM [de] YYYY');
};

/**
 * Capacity fill percent for the usage bar, clamped to 0-100. Unlike the
 * pre-acceptance timebar (which fills as a deadline approaches), this is the
 * literal capacity consumed. Non-finite input yields 0 (no fill) rather than NaN.
 */
export function computeCapacityFillPercent(capacityPct: number | null): number {
  if (typeof capacityPct !== 'number' || !Number.isFinite(capacityPct)) {
    return 0;
  }
  return Math.min(Math.max(capacityPct, 0), 100);
}

export type tShouldRenderUsageBannerArgs = {
  isNearLimitForOwner: boolean;
  isNearLimitForAdmin: boolean;
  isCappedForOwner: boolean;
  isCappedForAdmin: boolean;
  surface: tLeadAgentPaywallSurface | null;
};

/**
 * Renders when ANY of the four C8 role/phase flags is set AND the route maps to
 * a paywall surface. The flags are mutually exclusive (the provider sets at most
 * one — capped takes precedence over near-limit, owner over admin by role), so
 * OR is safe. A null surface (route off the paywall surfaces) hides it.
 */
export function shouldRenderUsageBanner(
  args: tShouldRenderUsageBannerArgs,
): boolean {
  const anyFlag =
    args.isNearLimitForOwner ||
    args.isNearLimitForAdmin ||
    args.isCappedForOwner ||
    args.isCappedForAdmin;
  return anyFlag && args.surface !== null;
}
